import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { SocialPostCard } from '@/components/social/SocialPostCard.tsx';

import type { PanelArtifact } from '../../../../../bridge/types/messages.ts';

/**
 * One saved post, playable, as it appears inside a chat thread.
 *
 * A thread renders this alone for a single shared post, and a set renders one
 * per member. The offer below the post sends a real customer message, so both
 * callers have to send the same sentence.
 */
export const SocialPostPreview = ({
	message,
	sourceUrl,
	canBuildCarousel,
	autoPlay,
	paused = false,
}: {
	message: PanelArtifact;
	/** The caller already tested this, so the component needs no guard. */
	sourceUrl: string;
	canBuildCarousel: boolean;
	autoPlay: boolean;
	paused?: boolean;
}) => {
	// Ask for the carousel in THIS chat, through the same action the composer
	// sends. A new chat would leave the customer looking at an empty thread and
	// lose the conversation the post was shared in.
	const bridgeApi = useBridgeApi();
	const buildCarousel = useCallback(() => {
		bridgeApi.sendAction({
			type: 'do_add_user_message_and_force_response',
			// `message.title` is deliberately NOT pasted in here. It is the record's
			// display label, which `_topic_label` cuts at 80 characters on a word
			// boundary, and it used to be the only design text the model ever saw —
			// so the slide stories were written from a fragment while the images were
			// drawn from the whole design read. A fragment in the request also reads
			// to the model like a topic instruction, which is worse than absent.
			// `link_item_id` below names the record, and the chat agent injects that
			// record's FULL design read (`build_linked_design_block`).
			content:
				'Build me an Instagram carousel in the style of this saved inspiration' +
				`${sourceUrl ? ` (${sourceUrl})` : ''}`,
			// Bind the thread to the card that was pressed. The message is plain
			// text, so without this the server re-resolves and prefers a
			// previously linked inspiration over the one this card names.
			link_item_id: message.item_id,
		});
	}, [bridgeApi, message.item_id, sourceUrl]);

	return (
		<SocialPostCard
			sourceUrl={sourceUrl}
			platform={message.source_post?.platform ?? ''}
			thumbnailUrl={message.source_post?.thumbnail_url}
			caption={message.title}
			autoPlay={autoPlay}
			paused={paused}
			hasUsableDesign={message.has_usable_design}
			// `!== false` on purpose: absent means unknown, which every message
			// written before the field existed carries, and those must keep their
			// button.
			onBuildCarousel={
				canBuildCarousel && message.has_usable_design !== false
					? buildCarousel
					: undefined
			}
		/>
	);
};
